import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Logo from "../assets/EditedPng.png";

export default function Loading() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const disableMotion = prefersReducedMotion || isMobile;


  // Animation variants
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const pulseVariants = disableMotion ? {} : {
    scale: [1, 1.05, 1],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      repeatType: "reverse"
    }
  };

  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden bg-primary-dark">
      {/* Background Image with Overlay - Matching Home Page */}
      <motion.div 
        className="absolute inset-0 z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: disableMotion ? 0 : 0.5 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/90 via-primary-dark/80 to-primary-dark/60 z-10" />
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transform scale-105"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop')",
          }}
        />
      </motion.div>

      <motion.div
        className="relative z-20 flex flex-col items-center"
        initial={disableMotion ? false : { opacity: 0 }}
        animate={disableMotion ? {} : { opacity: 1 }}
        transition={disableMotion ? {} : { duration: 0.5 }}
      >
        {/* Logo Container */}
        <motion.div
          className="relative bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 mb-8"
          initial={disableMotion ? false : { scale: 0.5, opacity: 0, rotate: -10 }}
          animate={disableMotion ? {} : {
            scale: 1,
            opacity: 1,
            rotate: 0,
            y: [0, -10, 0]
          }}
          transition={disableMotion ? {} : {
            scale: { duration: 0.8, ease: "easeOut" },
            opacity: { duration: 0.8, ease: "easeOut" },
            rotate: { duration: 0.8, ease: "easeOut" },
            y: { duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }
          }}
        >
          <motion.div
            className="absolute inset-0 rounded-xl border-2 border-transparent"
            animate={disableMotion ? {} : {
              borderColor: [
                "rgba(255,215,0,0.3)",
                "rgba(255,215,0,0.8)",
                "rgba(255,215,0,0.3)",
              ],
            }}
            transition={disableMotion ? {} : {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              background:
                "linear-gradient(45deg, transparent, white, gray, transparent)",
            }}
          />
          {!disableMotion && (
            <>
              <motion.div
                className="absolute inset-0 rounded-full border-4 border-secondary/20"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.4, 0.8, 0.4],
                }}
                transition={{
                  duration: 3,
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatType: "loop",
                }}
              />
              <motion.div
                className="absolute inset-0 rounded-full border-4 border-secondary/30"
                animate={{
                  scale: [1, 1.4, 1],
                  opacity: [0.2, 0.6, 0.2],
                }}
                transition={{
                  duration: 3.5,
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatType: "loop",
                  delay: 0.2,
                }}
              />
            </>
          )}
          <img
            src={Logo}
            alt="Nest Constructions Logo"
            className="w-32 h-32 md:w-40 md:h-40 object-contain filter drop-shadow-2xl"
          />
        </motion.div>

        {/* Brand Text */}
        <div className="flex flex-col items-center text-center mb-12">
          <div className="text-secondary font-heading font-medium text-sm md:text-base tracking-[0.4em] leading-none mb-2">
            KERALA
          </div>
          <div className="flex flex-col items-center">
            <span className="text-white font-heading font-semibold text-4xl md:text-5xl lg:text-6xl tracking-widest leading-none">
              NEST
            </span>
            <span className="text-white font-heading font-medium text-2xl md:text-3xl lg:text-4xl tracking-wide leading-none mt-1">
              CONSTRUCTIONS
            </span>
          </div>
        </div>

        {/* Loading Indicator */}
        <div className="w-64 h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-secondary"
            initial={disableMotion ? false : { width: "0%" }}
            animate={disableMotion ? {} : { width: "100%" }}
            transition={disableMotion ? {} : {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        <motion.p
          className="text-white/50 text-xs tracking-widest mt-4 font-light"
          initial={disableMotion ? false : { opacity: 0.5 }}
          animate={disableMotion ? {} : { opacity: [0.5, 1, 0.5] }}
          transition={disableMotion ? {} : { duration: 1.5, repeat: Infinity }}
        >
          LOADING EXPERIENCE...
        </motion.p>
      </motion.div>
    </div>
  );
}
